import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TeamMemberData, BandPercentages } from "../types";
import { useBandingSimulation } from "./useBandingSimulation";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

export const useBandingData = () => {
  const [teamMembersData, setTeamMembersData] = useState<TeamMemberData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bandBoundaries, setBandBoundaries] = useState({
    line1: 2.5,
    line2: 3.0,
    line3: 3.5,
    line4: 5.0
  });
  const [bandPercentages, setBandPercentages] = useState<BandPercentages>({
    A: 10,
    B: 25,
    C: 55,
    D: 10
  });
  const [bellCurveRefreshTrigger, setBellCurveRefreshTrigger] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { activeYear } = usePerformanceYear();

  const { saveBandingSimulation, resetBandingSimulation } = useBandingSimulation(currentUser);

  // Function to calculate allocated band based on boundaries and rating
  const calculateAllocatedBand = (rating: number, boundaries: any): string => {
    if (rating === 0) return '-';
    if (rating < boundaries.line1) return 'D';
    if (rating < boundaries.line2) return 'C';
    if (rating < boundaries.line3) return 'B';
    return 'A';
  };

  // Helper function to check if a status should lock the band value
  const isStatusLocked = (status: string | null | undefined): boolean => {
    return status === 'Final Band Accepted' || 
           status === 'Band disagreement submitted' || 
           status === 'Band disagreement resolution completed';
  };

  const fetchCurrentUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('Error fetching current user:', userError);
        return null;
      }

      const { data: userData, error: userDataError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (userDataError) {
        console.error('Error fetching user data:', userDataError);
        return null;
      }

      console.log('useBandingData: Current user fetched:', userData);
      return userData;
    } catch (error) {
      console.error('Error in fetchCurrentUser:', error);
      return null;
    }
  };

  const fetchTeamMembersData = async (supervisor: any) => {
    if (!supervisor) {
      console.log('useBandingData: No supervisor provided');
      setTeamMembersData([]);
      return;
    }

    setIsLoading(true);
    try {
      console.log('useBandingData: Fetching team members for supervisor:', supervisor.id);

      // Get direct reports only (not transitive)
      const { data: directReports, error: reportsError } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('supervisor_id', supervisor.id);

      if (reportsError) {
        console.error('useBandingData: Error fetching direct reports:', reportsError);
        setTeamMembersData([]);
        return;
      }

      if (!directReports || directReports.length === 0) {
        console.log('useBandingData: No direct reports found');
        setTeamMembersData([]);
        return;
      }

      console.log('useBandingData: Direct reports found:', directReports);

      const teamMemberIds = directReports.map(report => report.id);

      // Fetch performance appraisals to calculate averages - filter by activeYear
      const { data: allAppraisals, error: appraisalsError } = await supabase
        .from('performance_appraisals')
        .select('user_id, self_rating, manager_rating')
        .in('user_id', teamMemberIds)
        .eq('year', activeYear);

      if (appraisalsError) {
        console.error('useBandingData: Error fetching performance appraisals:', appraisalsError);
      }

      console.log('useBandingData: Performance appraisals fetched:', allAppraisals);

      // CRITICAL: Fetch existing performance bands - READ ONLY, NO MODIFICATIONS - filter by activeYear
      const { data: existingPerformanceBands, error: bandsError } = await supabase
        .from('performance_bands')
        .select('employee_id, self_rating, manager_rating, allocated_performance_band, final_banding, supervisor_banding_comments, status')
        .in('employee_id', teamMemberIds)
        .eq('year', activeYear);

      if (bandsError) {
        console.error('useBandingData: Error fetching existing performance bands:', bandsError);
      }

      console.log('useBandingData: Existing performance bands fetched (READ ONLY):', existingPerformanceBands);

      const processedTeamData: TeamMemberData[] = [];

      for (const member of directReports) {
        console.log(`useBandingData: Processing team member: ${member.full_name} (ID: ${member.id})`);
        
        // Calculate average ratings from performance appraisals
        const memberAppraisals = allAppraisals?.filter(appraisal => appraisal.user_id === member.id) || [];
        
        let calculatedSelfRating = 0;
        let calculatedManagerRating = 0;
        
        if (memberAppraisals.length > 0) {
          const selfRatings = memberAppraisals
            .map(a => a.self_rating)
            .filter(rating => rating !== null && rating !== undefined);
          
          const managerRatings = memberAppraisals
            .map(a => a.manager_rating)
            .filter(rating => rating !== null && rating !== undefined);
          
          if (selfRatings.length > 0) {
            calculatedSelfRating = Number((selfRatings.reduce((sum, rating) => sum + rating, 0) / selfRatings.length).toFixed(2));
          }
          
          if (managerRatings.length > 0) {
            calculatedManagerRating = Number((managerRatings.reduce((sum, rating) => sum + rating, 0) / managerRatings.length).toFixed(2));
          }
        }

        console.log(`useBandingData: Calculated ratings for ${member.full_name}: Self=${calculatedSelfRating}, Manager=${calculatedManagerRating}`);

        // Find existing performance band data - PURE READ ONLY
        const existingBandData = existingPerformanceBands?.find(band => band.employee_id === member.id);
        
        // CRITICAL: READ EXISTING STATUS WITHOUT ANY MODIFICATIONS
        const preservedStatus = existingBandData?.status || null;
        
        console.log(`useBandingData: PRESERVED STATUS for ${member.full_name}: "${preservedStatus}" - NO CHANGES MADE`);

        // Use existing database values if available, otherwise use calculated ratings
        const finalSelfRating = existingBandData?.self_rating || calculatedSelfRating || 0;
        const finalManagerRating = existingBandData?.manager_rating || calculatedManagerRating || 0;
        
        // Calculate system banding based on manager rating (or self rating if no manager rating)
        const ratingForBanding = finalManagerRating || finalSelfRating || 0;
        let systemBanding = existingBandData?.allocated_performance_band || '-';
        if (ratingForBanding > 0 && !existingBandData?.allocated_performance_band) {
          systemBanding = calculateAllocatedBand(ratingForBanding, { line1: 2.5, line2: 3.0, line3: 3.5 });
        }

        const teamMemberData: TeamMemberData = {
          id: member.id,
          full_name: member.full_name || 'Unknown',
          averageSelfRating: finalSelfRating,
          averageManagerRating: finalManagerRating,
          finalBandingScore: Number((finalManagerRating || finalSelfRating || 0).toFixed(2)),
          allocatedPerformanceBand: systemBanding,
          finalBanding: existingBandData?.final_banding || systemBanding,
          supervisorBandingComments: existingBandData?.supervisor_banding_comments || '',
          isCommentEditable: false,
          hasSavedRecord: !!existingBandData,
          status: preservedStatus // PRESERVE EXISTING STATUS - NO MODIFICATIONS
        };

        processedTeamData.push(teamMemberData);
        console.log(`useBandingData: Team member processed with PRESERVED status "${preservedStatus}":`, teamMemberData);
      }

      console.log('useBandingData: Final team members data with PRESERVED statuses:', processedTeamData);
      setTeamMembersData(processedTeamData);
    } catch (error) {
      console.error('useBandingData: Error fetching team members data:', error);
      setTeamMembersData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to update team members data based on new boundaries - only affects finalBanding (adjusted banding)
  // BUT preserve locked bands (Accepted, Disagreement, Resolved status)
  const updateTeamMembersWithBoundaries = (boundaries: any) => {
    const safeBoundaries = {
      ...boundaries,
      line4: boundaries.line4 || 5.0
    };
    
    setTeamMembersData(prevData => 
      prevData.map(member => {
        const score = member.averageManagerRating;
        let newAdjustedBanding = member.finalBanding; // Keep current adjusted banding as default
        
        // Only recalculate adjusted banding if we have a valid score AND the status is not locked
        if (score > 0 && !isStatusLocked(member.status)) {
          newAdjustedBanding = calculateAllocatedBand(score, safeBoundaries);
        }
        
        return {
          ...member,
          // allocatedPerformanceBand stays the same (system banding)
          finalBanding: newAdjustedBanding // This is the adjusted banding that changes only if not locked
        };
      })
    );
  };

  const handleBoundariesChange = (boundaries: any) => {
    const safeBoundaries = {
      ...boundaries,
      line4: boundaries.line4 || 5.0
    };
    setBandBoundaries(safeBoundaries);
    updateTeamMembersWithBoundaries(safeBoundaries);
  };

  const handlePercentageChange = (band: keyof BandPercentages, value: number[]) => {
    const newPercentages = {
      ...bandPercentages,
      [band]: value[0]
    };
    setBandPercentages(newPercentages);
  };

  const handlePercentageCommit = async (band: keyof BandPercentages, value: number[]) => {
    console.log(`useBandingData: Band ${band} committed with value: ${value[0]}`);
    await saveBandingSimulation(bandPercentages);
  };

  const resetBoundaries = async () => {
    const defaultBoundaries = { line1: 2.5, line2: 3.0, line3: 3.5, line4: 5.0 };
    const defaultPercentages = { A: 10, B: 25, C: 55, D: 10 };
    
    setBandBoundaries(defaultBoundaries);
    setBandPercentages(defaultPercentages);
    setBellCurveRefreshTrigger(prev => prev + 1);
    
    updateTeamMembersWithBoundaries(defaultBoundaries);
    
    await resetBandingSimulation();
    console.log('useBandingData: Boundaries reset to default');
  };

  const getBandStatistics = () => {
    if (!teamMembersData || teamMembersData.length === 0) {
      return {
        A: { count: 0, percentage: 0 },
        B: { count: 0, percentage: 0 },
        C: { count: 0, percentage: 0 },
        D: { count: 0, percentage: 0 }
      };
    }

    // Calculate based on finalBanding (adjusted banding) not allocatedPerformanceBand (system banding)
    const bandCounts = teamMembersData.reduce((acc, member) => {
      const band = member.finalBanding; // Use adjusted banding for statistics
      if (band === 'A') acc.bandA++;
      else if (band === 'B') acc.bandB++;
      else if (band === 'C') acc.bandC++;
      else if (band === 'D') acc.bandD++;
      return acc;
    }, { bandA: 0, bandB: 0, bandC: 0, bandD: 0 });

    const totalMembers = teamMembersData.length;

    return {
      A: { 
        count: bandCounts.bandA, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandA / totalMembers) * 100) : 0 
      },
      B: { 
        count: bandCounts.bandB, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandB / totalMembers) * 100) : 0 
      },
      C: { 
        count: bandCounts.bandC, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandC / totalMembers) * 100) : 0 
      },
      D: { 
        count: bandCounts.bandD, 
        percentage: totalMembers > 0 ? Math.round((bandCounts.bandD / totalMembers) * 100) : 0 
      }
    };
  };

  // CRITICAL: Save banding function that NEVER modifies status for locked records
  const saveBandingToDatabase = async (membersData: TeamMemberData[]) => {
    if (!currentUser) {
      console.error('useBandingData: No current user available for saving');
      return { success: false, error: 'No current user' };
    }

    try {
      console.log('useBandingData: Starting save banding operation');

      const savePromises = membersData.map(async (member) => {
        console.log(`useBandingData: Processing save for ${member.full_name} with status: "${member.status}"`);

        // CRITICAL: For members with locked status, only update comments and final_banding if not locked
        // Never update the status field for any reason
        let updateData: any = {
          supervisor_banding_comments: member.supervisorBandingComments,
          updated_at: new Date().toISOString()
        };

        // Only update final_banding for unlocked records
        if (!isStatusLocked(member.status)) {
          updateData.final_banding = member.finalBanding;
          // Only set status to "Allocated Performance Band" if it's currently null/undefined
          if (!member.status) {
            updateData.status = 'Allocated Performance Band';
          }
        }

        console.log(`useBandingData: Update data for ${member.full_name}:`, updateData);

        // Try to update first
        const { data: updateResult, error: updateError } = await supabase
          .from('performance_bands')
          .update(updateData)
          .eq('employee_id', member.id)
          .eq('year', activeYear)
          .select();

        if (updateError) {
          console.error(`useBandingData: Error updating performance band for ${member.full_name}:`, updateError);
          return { success: false, error: updateError, memberId: member.id };
        }

        if (!updateResult || updateResult.length === 0) {
          // Record doesn't exist, create it only if status is not locked
          if (!isStatusLocked(member.status)) {
            const insertData = {
              employee_id: member.id,
              user_id: currentUser.id,
              supervisor_id: currentUser.id,
              year: activeYear,
              self_rating: member.averageSelfRating,
              manager_rating: member.averageManagerRating,
              allocated_performance_band: member.allocatedPerformanceBand,
              final_banding: member.finalBanding,
              supervisor_banding_comments: member.supervisorBandingComments,
              status: 'Allocated Performance Band'
            };

            console.log(`useBandingData: Creating new performance band record for ${member.full_name}:`, insertData);

            const { error: insertError } = await supabase
              .from('performance_bands')
              .insert(insertData);

            if (insertError) {
              console.error(`useBandingData: Error inserting performance band for ${member.full_name}:`, insertError);
              return { success: false, error: insertError, memberId: member.id };
            }
          } else {
            console.log(`useBandingData: Skipping insert for ${member.full_name} due to locked status: ${member.status}`);
          }
        }

        return { success: true, memberId: member.id };
      });

      const results = await Promise.all(savePromises);
      const failures = results.filter(r => !r.success);

      if (failures.length > 0) {
        console.error('useBandingData: Some saves failed:', failures);
        return { success: false, error: 'Some saves failed', failures };
      }

      console.log('useBandingData: All performance bands saved successfully');
      return { success: true };

    } catch (error) {
      console.error('useBandingData: Error in saveBandingToDatabase:', error);
      return { success: false, error };
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      console.log('useBandingData: Initializing data');
      const user = await fetchCurrentUser();
      setCurrentUser(user);
      
      if (user) {
        await fetchTeamMembersData(user);
      }
    };

    initializeData();
  }, [activeYear]);

  return {
    updatedTeamMembersData: teamMembersData,
    isLoading,
    currentUser,
    bandBoundaries,
    bandPercentages,
    bellCurveRefreshTrigger,
    handleBoundariesChange,
    handlePercentageChange,
    handlePercentageCommit,
    resetBoundaries,
    getBandStatistics,
    setTeamMembersData,
    saveBandingToDatabase
  };
};
